import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { v4 } from 'uuid'

import {
	UnavailableItemsModal,
	WarningBanner,
} from '@/components/NewPanelProperties/components'
import { useAppContext, useAppDispatch, useDeprecatedColumns } from '@/hooks'
import { EnhancedAsyncSelect } from '@/lib/components/Select/EnhancedAsyncSelect/EnhancedAsyncSelect'
import {
	TableColumn,
	TableConstraintColumn,
} from '@/rtkApi/mddApiSliceGenerated'
import { generateCode } from '@/store/utils'

import {
	AddIcon,
	Box,
	BoxColumn,
	BoxColumnFK,
	BoxFKs,
	ColumnTitle,
	FormInputFK,
	ItemText,
	PrimaryKey,
} from './styles'
import { ForeignKeyProps, NewColumnProps } from './types'
import { getFkSelectOptions } from './utils'

export const ForeignKey = ({
	columns,
	disabled,
	foreignKeyTableId,
	onChange,
	optionLabel,
	optionValue,
	possibleForeignKeys,
	primaryKeys,
	readonly,
}: ForeignKeyProps) => {
	const dispatch = useAppDispatch()
	const { t } = useAppContext()
	const { control } = useFormContext()
	const [newColumns, setNewColumns] = useState<NewColumnProps>({})

	const {
		showDeprecatedWarning,
		isModalOpen,
		handleOpenModal,
		handleCloseModal,
		enrichmentResult,
		handleRemoveDeprecatedColumns,
		pickedColumns,
	} = useDeprecatedColumns({
		tableColumns: columns,
	})

	const typedFields = pickedColumns as unknown as TableConstraintColumn[]

	const sortedForeignKeys = [...typedFields].sort((a, b) => a.order - b.order)

	const onAddNew = (pk: TableColumn) => {
		setNewColumns((prev) => ({
			...prev,
			[pk.uuid as string]: {
				isCreatingNewColumn: true,
				defaultValue: pk.name || '',
			},
		}))
	}

	const onClose = (pk: TableColumn) => {
		setNewColumns((prev) => ({
			...prev,
			[pk.uuid as string]: {
				isCreatingNewColumn: false,
				defaultValue: pk.name || '',
			},
		}))
	}

	const createColumn = async (nameColumn: string, pk: TableColumn) => {
		const codeApi = await dispatch(generateCode(nameColumn))

		onChange &&
			onChange([
				...sortedForeignKeys.filter((v) => v.foreignColumnUuid !== pk.uuid),
				{
					code: codeApi.result,
					uuid: v4(),
					name: nameColumn,
					foreignColumnCode: pk.code,
					foreignColumnUuid: pk.uuid,
				},
			])

		onClose(pk)
	}

	const onKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		pk: TableColumn,
	) => {
		switch (e.key) {
			case 'Enter': {
				if (e.currentTarget.value) {
					createColumn(e.currentTarget.value, pk)
				}

				break
			}

			case 'Escape': {
				onClose(pk)
			}
		}
	}

	if (!foreignKeyTableId) {
		if (!isEmpty(sortedForeignKeys)) {
			return (
				<ul>
					{sortedForeignKeys.map((fkConstraint) => {
						return (
							<li className="list-disc" key={fkConstraint?.uuid}>
								{fkConstraint?.code}
							</li>
						)
					})}
				</ul>
			)
		} else {
			return <>{t('TABLE_CONSTRAINTS_SELECT_TABLE')}</>
		}
	}

	if (!primaryKeys || !primaryKeys.length) {
		return <>{t('TABLE_CONSTRAINTS_NO_PRIMARY_KEYS')}</>
	}

	return (
		<div>
			{!disabled &&
				showDeprecatedWarning &&
				enrichmentResult.hasDeprecatedColumns && (
					<WarningBanner handleOpenModal={handleOpenModal} />
				)}

			{primaryKeys.map((pk, i) => {
				const isFirst = i === 0

				const { isCreatingNewColumn, defaultValue } = newColumns[
					pk?.uuid as string
				] ?? {
					isCreatingNewColumn: false,
					defaultValue: '',
				}

				const options = getFkSelectOptions({
					possibleForeignKeys,
					sortedForeignKeys: typedFields,
					primaryKey: pk,
				})

				return (
					<Box key={pk.uuid} data-testid={`box-${pk.name}`}>
						<BoxColumn>
							{isFirst && (
								<ColumnTitle>{t('TABLE_CONSTRAINTS_PRIMARY_KEY')}</ColumnTitle>
							)}
							<PrimaryKey title={pk.name}>
								<ItemText>{pk.name}</ItemText>
								<ItemText>{pk.dataType}</ItemText>
							</PrimaryKey>
						</BoxColumn>

						<BoxColumnFK>
							{isFirst && (
								<ColumnTitle>{t('TABLE_CONSTRAINTS_FOREIGN_KEY')}</ColumnTitle>
							)}

							<BoxFKs>
								{isCreatingNewColumn ? (
									<FormInputFK
										onKeyDown={(e) => onKeyDown(e, pk)}
										onBlur={() => onClose(pk)}
										autoFocus
										$fromProps
										defaultValue={defaultValue}
									/>
								) : (
									<div className="flex items-center w-full">
										<Controller
											control={control}
											name={`columns.${i}`}
											render={({ field }) => {
												// Merge current value into options if not already present
												const currentValue = field.value as TableConstraintColumn | undefined
												const valueUuid = currentValue?.uuid
												const isCurrentValueInOptions = options?.some(
													(opt) => opt.uuid === valueUuid
												)
												
												const mergedOptions = 
													currentValue && valueUuid && !isCurrentValueInOptions
														? [currentValue, ...(options || [])]
														: options || []

												return (
													<EnhancedAsyncSelect
														options={mergedOptions}
														isDisabled={disabled || readonly}
														labelKey={optionLabel as 'label'}
														valueKey={optionValue as 'value'}
														{...field}
														onChange={(selectedUuid) => {
															const selectedColumn = mergedOptions.find(
																(opt) => opt.uuid === selectedUuid
															)
															
															if (selectedColumn) {
																field.onChange(selectedColumn)
															} else if (!selectedUuid) {
																field.onChange(null)
															}
														}}
														value={currentValue?.uuid || ''}
														useBaseStyles
													/>
												)
											}}
										/>

										<AddIcon
											schema="default"
											onClick={() => onAddNew(pk)}
											disabled={disabled || readonly}
											title={t('ADD_NEW')}
											icon={faPlus}
										/>
									</div>
								)}
							</BoxFKs>
						</BoxColumnFK>
					</Box>
				)
			})}
			<UnavailableItemsModal
				open={isModalOpen}
				onClose={handleCloseModal}
				unavailableItems={enrichmentResult.deprecatedColumns}
				onConfirm={handleRemoveDeprecatedColumns}
			/>
		</div>
	)
}
